import api from './api';

export interface ProjectFile {
  id: number;
  project_id: number;
  name: string;
  type: 'file' | 'folder';
  file_type: 'image' | 'pdf' | 'document' | 'code' | 'archive' | 'other';
  path: string;
  storage_path: string;
  size: number;
  formatted_size: string;
  mime_type: string | null;
  tags: string[];
  parent_id: number | null;
  uploaded_by: number;
  is_shared: boolean;
  download_count: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  uploader?: {
    id: number;
    name: string;
    email: string;
  };
  parent_folder?: {
    id: number;
    name: string;
    path: string;
  };
}

export interface FileShare {
  id: number;
  file_id: number;
  shared_by: number;
  shared_with: number;
  permission: 'view' | 'download' | 'edit';
  expires_at: string | null;
  created_at: string;
  recipient?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface FileVersion {
  id: number;
  file_id: number;
  version: number;
  storage_path: string;
  size: number;
  uploaded_by: number;
  change_notes: string | null;
  created_at: string;
  uploader?: {
    id: number;
    name: string;
    email: string;
  };
}

export interface FileStatistics {
  total_files: number;
  total_folders: number;
  total_size: number;
  formatted_size: string;
  recent_uploads: number;
  shared_files: number;
  by_type: {
    image: number;
    pdf: number;
    document: number;
    code: number;
    archive: number;
    other: number;
  };
}

export interface FileFilters {
  project_id?: number;
  parent_id?: number | null;
  type?: 'file' | 'folder';
  file_type?: 'image' | 'pdf' | 'document' | 'code' | 'archive' | 'other';
  search?: string;
  tags?: string[];
  shared?: boolean;
  recent?: boolean;
  my_files?: boolean;
  sort_by?: 'name' | 'size' | 'created_at' | 'download_count';
  sort_order?: 'asc' | 'desc';
}

export interface FileUploadData {
  project_id: number;
  file: File;
  name?: string;
  parent_id?: number | null;
  tags?: string[];
}

export interface CreateFolderData {
  project_id: number;
  name: string;
  parent_id?: number | null;
}

export interface ShareFileData {
  user_ids: number[];
  permission: 'view' | 'download' | 'edit';
  expires_at?: string | null;
}

class FileManagementService {
  /**
   * Get files and folders with optional filters
   */
  async getFiles(filters: FileFilters = {}): Promise<ProjectFile[]> {
    const params = new URLSearchParams();
    
    if (filters.project_id) params.append('project_id', filters.project_id.toString());
    if (filters.parent_id !== undefined) {
      params.append('parent_id', filters.parent_id?.toString() || '');
    }
    if (filters.type) params.append('type', filters.type);
    if (filters.file_type) params.append('file_type', filters.file_type);
    if (filters.search) params.append('search', filters.search);
    if (filters.tags && filters.tags.length > 0) {
      params.append('tags', filters.tags.join(','));
    }
    if (filters.shared !== undefined) params.append('shared', filters.shared ? '1' : '0');
    if (filters.recent) params.append('recent', '1');
    if (filters.my_files) params.append('my_files', '1');
    if (filters.sort_by) params.append('sort_by', filters.sort_by);
    if (filters.sort_order) params.append('sort_order', filters.sort_order);

    const response = await api.get(`/project-manager/files?${params.toString()}`);
    return response.data.data;
  }

  /**
   * Upload a new file
   */
  async uploadFile(
    data: FileUploadData,
    onUploadProgress?: (progressEvent: any) => void
  ): Promise<ProjectFile> {
    const formData = new FormData();
    formData.append('project_id', data.project_id.toString());
    formData.append('file', data.file);
    
    if (data.name) formData.append('name', data.name);
    if (data.parent_id !== undefined && data.parent_id !== null) {
      formData.append('parent_id', data.parent_id.toString());
    }
    if (data.tags && data.tags.length > 0) {
      data.tags.forEach(tag => formData.append('tags[]', tag));
    }

    const response = await api.post('/project-manager/files', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    });
    
    return response.data.data;
  }

  /**
   * Create a new folder
   */
  async createFolder(data: CreateFolderData): Promise<ProjectFile> {
    const response = await api.post('/project-manager/files/folder', {
      project_id: data.project_id,
      name: data.name,
      parent_id: data.parent_id || null,
    });
    
    return response.data.data;
  }

  /**
   * Download a file
   */
  async downloadFile(fileId: number): Promise<Blob> {
    const response = await api.get(`/project-manager/files/${fileId}/download`, {
      responseType: 'blob',
    });
    
    return response.data;
  }

  /**
   * Get download URL for a file (opens in browser)
   */
  getDownloadUrl(fileId: number): string {
    const baseUrl = api.defaults.baseURL || '';
    return `${baseUrl}/project-manager/files/${fileId}/download`;
  }

  /**
   * Delete a file or folder (soft delete)
   */
  async deleteFile(fileId: number): Promise<void> {
    await api.delete(`/project-manager/files/${fileId}`);
  }

  /**
   * Share a file with other users
   */
  async shareFile(fileId: number, data: ShareFileData): Promise<FileShare[]> {
    const response = await api.post(`/project-manager/files/${fileId}/share`, data);
    return response.data.data;
  }

  /**
   * Get file statistics
   */
  async getStatistics(projectId?: number): Promise<FileStatistics> {
    const params = projectId ? `?project_id=${projectId}` : '';
    const response = await api.get(`/project-manager/files/statistics${params}`);
    return response.data.data;
  }

  /**
   * Helper: Format file size
   */
  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Helper: Get file icon based on file type
   */
  getFileIcon(fileType: string): string {
    const icons: Record<string, string> = {
      image: 'image',
      pdf: 'picture_as_pdf',
      document: 'description',
      code: 'code',
      archive: 'folder_zip',
      other: 'insert_drive_file',
      folder: 'folder',
    };
    
    return icons[fileType] || icons.other;
  }

  /**
   * Helper: Get file type color
   */
  getFileTypeColor(fileType: string): string {
    const colors: Record<string, string> = {
      image: '#4CAF50',
      pdf: '#F44336',
      document: '#2196F3',
      code: '#9C27B0',
      archive: '#FF9800',
      other: '#757575',
      folder: '#FFC107',
    };
    
    return colors[fileType] || colors.other;
  }

  /**
   * Helper: Download file and trigger browser download
   */
  async triggerDownload(fileId: number, fileName: string): Promise<void> {
    try {
      const blob = await this.downloadFile(fileId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      throw error;
    }
  }
}

export default new FileManagementService();
